import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { selectEulaPath, selectEulaStatus } from '@store/user/user.selector';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DisplayOptions } from '@store/user/user.model';
import { EulaService } from '@shared/services/eula.service';
import { RootRoutes } from '@root/root-routes';
import { EulaModalResponse } from '@store/user/user.actions';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { ActionName } from '@shared/models/effects-names.model';

@Injectable({
  providedIn: 'root'
})
export class EulaGuard  {
  constructor(
    private readonly store$: Store<AppState>,
    private readonly router: Router,
    private readonly eulaService: EulaService,
    private readonly ribbonService: RibbonService,
    private readonly spinnerOverlayService: SpinnerOverlayService) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.store$
      .pipe(
        select(selectEulaStatus),
        filter((status): status is DisplayOptions => status !== undefined),
        take(1),
        switchMap((status: DisplayOptions) => status === DisplayOptions.NO ? this.confirmEula() : of(true)),
        map((active: boolean) =>  active || this.router.createUrlTree([RootRoutes.Login]))
      );
  }

  private confirmEula(): Observable<boolean> {
    return this.store$.pipe(
      select(selectEulaPath),
      filter((path): path is string => !!path),
      take(1),
      this.spinnerOverlayService.wrapRequest(
        (eulaPath: string) => this.eulaService.getEulaText(eulaPath),
        ActionName.EULA_GUARD
      ),
      switchMap((agreement: string) => this.eulaService.openEulaModal(agreement)),
      catchError(() => {
        this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please refresh the page. And try again.');
        return of(false);
      }),
      tap((modalResponse: boolean) => this.store$.dispatch(new EulaModalResponse(modalResponse)))
    );
  }
}
